import React from "react";
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
  Box,
  Tooltip,
} from "@mui/material";
import { FiberManualRecord } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  formatLastUpdated,
  getBatteryColor,
  getBatteryIcon,
  getConnectionColor,
  getConnectionIcon,
  getConnectionTooltip,
  getRobotStatus,
  isDataStale,
} from "./deviceStatusUtils";

// Memoize the component to prevent unnecessary re-renders
const DeviceCard = React.memo(function DeviceCard({
  device,
  size,
  buttonText,
  urlPrefix = "/device/",
  disabled = true,
}) {
  const navigate = useNavigate();

  const handleDetailsClick = () => {
    navigate(`${urlPrefix}${device.rc_device_id}`);
    console.log(`Navigating to: ${urlPrefix}${device.rc_device_id}`);
  };

  // Connection status
  const deviceOnline = device.connected === 1 || device.connected === true;

  // Get overall robot status
  const robotStatus = getRobotStatus(device);
  const isStale = robotStatus.isStale;

  const batteryLevel = device.battery_level || 0;
  const BatteryIcon = getBatteryIcon(batteryLevel, device);
  const batteryColor = getBatteryColor(batteryLevel, deviceOnline, isStale);

  // Connection status with stale data handling
  const ConnectionIcon = getConnectionIcon(deviceOnline, isStale);
  const connectionStatusColor = getConnectionColor(deviceOnline, isStale);
  const connectionStatusText = getConnectionTooltip(deviceOnline, isStale);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3, p: 2, position: "relative" }}>
      {/* Status Indicators */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {/* Battery Indicator */}
        <Tooltip
          title={
            isStale
              ? "Battery level unknown (stale data)"
              : `Battery: ${batteryLevel}%`
          }
          arrow
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <BatteryIcon
              sx={{
                fontSize: 18,
                color: batteryColor,
              }}
            />
            {!isStale && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: "bold",
                  color: batteryColor,
                  lineHeight: 1,
                }}
              >
                {batteryLevel}%
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Connection Status Indicator */}
        <Tooltip title={connectionStatusText} arrow>
          <ConnectionIcon
            sx={{
              fontSize: 16,
              color: connectionStatusColor,
            }}
          />
        </Tooltip>

        {/* Overall Robot Status Indicator */}
        <Tooltip
          title={
            robotStatus.isStale
              ? `No data since ${formatLastUpdated(robotStatus.lastUpdated)}`
              : robotStatus.status
          }
          arrow
        >
          <FiberManualRecord
            sx={{
              fontSize: 16,
              color: robotStatus.color,
              opacity: robotStatus.isStale ? 0.6 : 1,
            }}
          />
        </Tooltip>
      </Box>

      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {device.name || "Unnamed Device"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          UUID: {device.rc_device_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          S/N: {device.uk_device_id}
        </Typography>
        {size === "medium" ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Status: {device.operating_state || "Unknown"}
            </Typography>
            {device.manufacturer === "Bosch" && (
              <Typography variant="body2" color="text.secondary">
                Cleaning Step: {device.cleaning_step || "N/A"}
              </Typography>
            )}
            {device.manufacturer !== "Bosch" && (
              <Typography variant="body2" color="text.secondary">
                State: {device.movement_state || "Unknown"}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Type: {device.model || "N/A"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer: {device.customer_name || "Unassigned"}
            </Typography>
          </>
        ) : null}
      </CardContent>
      <CardActions>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={handleDetailsClick}
        >
          {buttonText || "Details"}
        </Button>
      </CardActions>
    </Card>
  );
});

export default DeviceCard;
